<script>
   /**************************************************************/
   /* PCVCalculator                                              */
   /* -------------------------                                  */
   /* component for computing a PV-set                           */
   /**************************************************************/

   import { untrack } from 'svelte';
   import { Select, Container, Button, Spinner, ButtonCancel, ButtonDownload } from 'svelte-controls-basic';
   import { pcvpca } from '@mdatools-apps/wasm/pca.js';
   import { downloadData, app_pca_predict } from '../../utils.js';

   // main properties of the whole app
   let {
      WASM,
      model,
      calset,
      calres,
      outliers = [],
      pvset = $bindable(),
      pvres = $bindable()
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   // error handling parameetrs
   let status = $state('neutral');
   let errMsg = $state('');

   // promise container for calculator
   let promise = $state();

   // compute number of segments options based on dataset size. untrack marks the capture-at-mount
   // as deliberate: every path which changes 'calset' or 'outliers' also clears the model, and
   // AppContainer only shows this component while the model is there, so it is unmounted and made
   // again rather than being given new props
   const nsegOptions = untrack(() => {
      if (!calset || calset.values.nrows < 1) return [];
      const n = calset.values.nrows - outliers.length;
      return [3, 4, 5, 10, 20].filter(v => v >= (n / (n - model.ncomp - 1)) && v <= n / 2);
   });

   // the split printed next to the set is the one it was made with, kept on it by 'computePVSet'.
   // the controls' values used to be printed, which are the defaults once a session is restored
   const cvInfo = $derived(!pvset || !pvset.cv ? '' :
      ', ' + pvset.cv.type + (pvset.cv.type !== 'loo' ? ' ' + pvset.cv.nseg + ' seg' : ''));

   // PV-set calculation parameters — if no segment options, limit to LOO
   let cvtypeOptions = $state(nsegOptions.length === 0 ? ['loo'] : ['ven', 'rand', 'loo']);
   let cvType = $state(nsegOptions.length === 0 ? 'loo' : 'ven');
   let nseg = $state(nsegOptions.length > 0 ? nsegOptions[0] : 0);

   // function that computes PV-set using given parameters
   async function computePVSet(calset, model, cv) {
      promise = WASM()
         .then(Module => {
            const cv_type = cv.type == 'loo' ? 0 : cv.type == 'rand' ? 2 : 1;
            const pvvalues = pcvpca(Module, model, calset.values, model.ncomp, cv.nseg, cv_type, 0, outliers);

            pvset = {

               classLabelsVarname: calset.classLabelsVarname,
               // the names come from the calibration result and not from the set: a group whose
               // last object was excluded is gone from 'calres.classNames', and taking the names
               // from the unfiltered set would leave it in the legend of a pv plot shown on its
               // own, with a colour map one group too long
               classNames: calres.classNames,
               cv: cv,
               dec: calset.dec,
               fileName: calset.fileName + '-pcv',
               hasClassNames: calset.hasClassNames,
               hasLabels: calset.hasLabels,
               header: calset.header,
               labelsVarname: calset.labelsVarname,

               classes: calres.classes,
               objindices: calres.objindices,
               objlabels: calres.objlabels,
               responses: calres.responses,
               sep: calset.sep,
               values: pvvalues,
               varAxisTitle: calset.varAxisTitle,

               varindices: model.varindices,
               varlabels: model.varlabels,
               varrev: model.varrev,
               varvalues: model.varvalues,
               varvaluesName: model.varvaluesName,
               varvaluesUnits: model.varvaluesUnits,
            }

            // pv-set is created for outliers free data, hence outliers is empty here
            pvres = app_pca_predict(Module, model, pvset, []);
            // the gate below reads it: a set the parent clears would otherwise bring nothing back
            status = 'neutral';
         })
         .catch((e) => {
            console.error(e)
            pvset = null;
            pvres = null;
            status = 'error';
            errMsg = e;
         });
   }

   // event handler which triggers the model computation. the controls are hidden while the run
   // is in flight, as in 'ModelCalculator': every call of 'WASM' instantiates the module anew,
   // and a second click in that time started a second run
   function compute(e) {
      status = 'computing';
      errMsg = '';
      computePVSet(calset, model, {type: cvType, nseg: nseg});
   }

   // function which resets the state of the component
   function reset() {
      status = 'neutral';
      errMsg = '';
      promise = null;
      pvset = null;
      pvres = null;
   }
</script>


{#if model && !pvset && status !== 'computing'}
   <Container name="split" label='Split type:' id="{uid}-cvtype">
      <Select bind:value={cvType} options={cvtypeOptions} />
   </Container>

   {#if cvType !== 'loo'}
   <Container name="nseg" label='Segments:' id="{uid}-nseg">
      <Select bind:value={nseg} options={nsegOptions} />
   </Container>
   {/if}

   <Container name="run-button" label=' '>
      <Button onclick={compute} text="Compute PV-set" />
   </Container>
{/if}

{#if promise}

   {#await promise}
      <Container name="spinner">
         <Spinner />
         <p>Computing PV-set...</p>
      </Container>
   {/await}

{/if}

{#if pvset }
<Container name="info">
   <p><span style="color:#a0a0a0;">PV-set:</span>
      {pvset.values.nrows} &times; {pvset.values.ncols}{cvInfo}</p>
   <ButtonDownload onclick={() => downloadData(pvset, calset.fileName + '-pv')} />
   <ButtonCancel onclick={reset} />
</Container>
{/if}

{#if status === 'error' && errMsg}
   <Container name="error" status='error'>
      <p>{errMsg}</p>
   </Container>
{/if}

